import Layout from "../../components/Layout";
import { Helmet } from "react-helmet-async";
import {
  FiClipboard,
  FiCalendar,
  FiClock,
  FiUsers,
  FiBarChart2,
  FiFileText,
  FiSettings,
  FiLayers,
  FiCheckCircle,
} from "react-icons/fi";

const ModuleProjectManagement = () => {
  const trackCTA = () => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "click", {
        event_category: "CTA",
        event_label: "Sign Up - Project Management Page",
      });
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Project Management | RedERP</title>
        <meta
          name="description"
          content="Organize, track, and deliver projects with RedERP’s Project Management module. Plan, assign, and monitor everything in one workspace."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-10 relative">
          {/* Text Section */}
          <div className="flex-1 text-left mt-16">
            <h1 className="text-4xl font-bold mb-4">Project Management</h1>
            <p className="mb-6 text-sm sm:text-base">
              Plan smarter, execute faster. Manage teams, timelines, budgets, and deliverables from one ERP-integrated platform.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Start Free Trial
            </a>
          </div>

          {/* Image */}
          <div className="flex-1 hidden md:flex justify-end mt-12 mb-0">
            <img
              src="/assets/images/project1.png"
              alt="Project Management Illustration"
              className="w-[420px] lg:w-[420px] xl:w-[420px] h-[310px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-left">
          {[
            {
              icon: <FiClipboard size={24} />,
              title: "Project Planning",
              desc: "Define milestones, tasks, dependencies, and resource allocations with timelines.",
            },
            {
              icon: <FiCalendar size={24} />,
              title: "Task Scheduling",
              desc: "Assign responsibilities with deadlines and monitor task completion progress.",
            },
            {
              icon: <FiClock size={24} />,
              title: "Time Tracking",
              desc: "Log hours per task or project phase. Ideal for billing, payroll, and performance tracking.",
            },
            {
              icon: <FiUsers size={24} />,
              title: "Team Collaboration",
              desc: "Empower your teams with shared dashboards, notes, and internal chat threads.",
            },
            {
              icon: <FiBarChart2 size={24} />,
              title: "Project Reports & KPIs",
              desc: "Get insights on progress, costs, delays, risks, and resource utilization.",
            },
            {
              icon: <FiFileText size={24} />,
              title: "Document & File Sharing",
              desc: "Attach contracts, blueprints, or supporting files to each project or milestone.",
            },
            {
              icon: <FiCheckCircle size={24} />,
              title: "Approval Workflows",
              desc: "Control phase progression with internal or client-side approvals and signoffs.",
            },
            {
              icon: <FiLayers size={24} />,
              title: "Multi-Project View",
              desc: "Manage multiple projects at once, including backlog and scheduled tasks.",
            },
            {
              icon: <FiSettings size={24} />,
              title: "Custom Project Templates",
              desc: "Save reusable workflows and templates for recurring project types or clients.",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3 text-primary mb-3">
                {item.icon}
                <h3 className="font-bold">{item.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white text-center">
        <div className="max-w-xl mx-auto px-6">
          <h3 className="text-3xl font-bold mb-4">Keep Your Projects on Track</h3>
          <p className="mb-6">Try RedERP Project Management — no credit card needed.</p>
          <a
            href="https://app.rederp.pro/sign-up"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
          >
            Try Project Module
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default ModuleProjectManagement;
